import { useAuth } from '../../application/account/AuthProvider';
import { useTranslate } from '../../application/i18n/LocaleProvider';
import { BOARD_WINDOW } from '../../domain/game/leaderboardEntry';

/**
 * The board that means other people (SPEC-009 PR-9.5).
 *
 * It sits directly under `DuelBoard`, and the two are deliberately not merged.
 * They are different promises: that one is the five best runs on THIS DEVICE,
 * including runs played before there was an account, and it survives signing out
 * untouched (SPEC-007, amended). This one is everybody. Collapsing them into a
 * single "leaderboard" that changed meaning depending on whether you were signed
 * in is exactly the small lie `DuelBoard`'s own label was written to avoid.
 *
 * For a signed-out reader this is ONE LINE and a button. That restraint is the
 * feature's whole posture: the duel is complete without an account, the offer
 * appears only under a run that has just ended, and it never becomes a wall
 * (SPEC-009). If this component ever grows a second sentence for a signed-out
 * reader, something has gone wrong with the argument.
 *
 * Every state is drawn, including the two nobody plans for — a board that cannot
 * be reached, and a board with nothing on it. This is a black screen, where a
 * blank panel is indistinguishable from a broken one, and it is the first
 * surface in the app that depends on a third-party origin the service worker
 * cannot cache (ADR-0005): offline, this fails, and it has to fail in words.
 */
export default function DuelLeaderboard({ board, onJoin }) {
    const t = useTranslate();
    const { isAuthenticated } = useAuth();

    if (!isAuthenticated) {
        return (
            <div className="duel-global duel-global--offer">
                <p className="duel-global__offer">{t('duel.global.offer')}</p>

                <button type="button" className="button button--quiet duel-global__join" onClick={onJoin}>
                    {t('duel.global.join')}
                </button>
            </div>
        );
    }

    return (
        <div className="duel-global">
            <p className="duel-global__title">{t('duel.global.title')}</p>

            <BoardRows board={board} />

            {/* What just happened to THIS run, said in words. A row marked in
                the ramp is invisible to anybody reading by ear, and a reader
                below the cut has no row to mark at all. */}
            <p className="duel-global__standing" role="status">
                <PublishOutcome board={board} />
            </p>
        </div>
    );
}

/**
 * The rows, or the reason there are none.
 *
 * `failed` says where the run is safe rather than only what broke: the run has
 * already been banked on the device board above, so the honest message is
 * reassurance, not an error the reader is expected to act on.
 */
function BoardRows({ board }) {
    const t = useTranslate();

    if (board.status === 'loading') {
        return <p className="duel-global__note">{t('duel.global.loading')}</p>;
    }

    if (board.status === 'failed') {
        return <p className="duel-global__note">{t('duel.global.failed')}</p>;
    }

    if (board.rows.length === 0) {
        return <p className="duel-global__note">{t('duel.global.empty')}</p>;
    }

    return (
        <ol className="duel-global__rows">
            {board.rows.map((row) => (
                <li
                    className={`duel-global__row${row.isMine ? ' duel-global__row--mine' : ''}`}
                    key={row.uid}
                >
                    <span className="duel-global__rank figure">{row.rank}</span>
                    <span className="duel-global__name">{row.displayName}</span>
                    <span className="duel-global__score figure">{row.score}</span>
                </li>
            ))}
        </ol>
    );
}

/**
 * Where this run landed, in one sentence.
 *
 * The `unbeaten` case is the one worth getting right: the reader did not fail at
 * anything, they simply did not beat themselves, and the sentence says what
 * their record still is rather than what this run was not. `failed` again names
 * where the run is safe — the device board has it either way.
 */
function PublishOutcome({ board }) {
    const t = useTranslate();
    const { publishStatus, standing, outsideWindow } = board;

    if (publishStatus === 'publishing') {
        return t('duel.global.publishing');
    }

    if (publishStatus === 'failed') {
        return t('duel.global.publishFailed');
    }

    if (publishStatus === 'unbeaten' && standing) {
        return t('duel.global.unbeaten', { score: standing.entry.score });
    }

    if (!standing) {
        return null;
    }

    // A reader past the fetched window keeps their score and loses only the
    // number beside it — this component never invents a rank (SPEC-009 D-9).
    if (outsideWindow || standing.rank === null) {
        return t('duel.global.outside', { total: BOARD_WINDOW, score: standing.entry.score });
    }

    return t('duel.global.standing', { rank: standing.rank, score: standing.entry.score });
}
